// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { catalogContainerStyles } from "@bg/XDL/Utilities/catalogStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    view: state.pages.productList.view,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface ProductListColumnsStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnTableViewGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnTableViewGridItem?: GridItemProps;
    accordionSection?: AccordionSectionPresentationProps;
    wrapper?: InjectableCss;
}

export const columnsStyles: ProductListColumnsStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                padding: 0;
                border: 0;
            `,
        },
        headerProps: {
            css: css`
                background: ${getColor("white")};
                margin-bottom: 12px;

                button {
                    background: none;
                    display: flex;
                    justify-content: space-between;
                    &,
                    &:focus {
                        padding: 0 10px 0 0 !important;
                        border: 0 !important;
                    }

                    > span:nth-child(1) {
                        font-size: 15px;
                        font-weight: bold;
                        letter-spacing: 0.23px;
                        line-height: 20px;
                        text-transform: uppercase;
                    }

                    > span:nth-child(2) {
                        border: 0;
                        background: ${getColor("white")};
                        width: 24px;
                        height: 24px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        &,
                        > * {
                            position: static;
                        }

                        svg {
                            width: 18px;
                            height: 18px;
                            color: ${getColor("secondary")};
                        }
                    }
                }
            `,
            "aria-level": 2,
        },
    },
    wrapper: {
        css: css`
            margin-bottom: 30px;
            padding-top: 17px;
            border-top: 2px solid ${getColor("common.ui3")};
        `,
    },
};

const styles = columnsStyles;

const ProductListColumns: FC<Props> = ({ id, view }) => {
    return (
        <StyledWrapper
            css={css`
                margin-top: 40px;
            `}
        >
            <StyledWrapper {...catalogContainerStyles.container}>
                <StyledWrapper {...catalogContainerStyles.leftColumn}>
                    <StyledWrapper {...columnsStyles.wrapper}>
                        <ManagedAccordionSection
                            title={translate("Filters")}
                            {...columnsStyles.accordionSection}
                            initialExpanded={true}
                        >
                            <Zone contentId={id} zoneName="leftContent" />
                        </ManagedAccordionSection>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...catalogContainerStyles.leftColumn} className="mobile-only">
                    <StyledWrapper {...columnsStyles.wrapper}>
                        <ManagedAccordionSection
                            title={translate("Filters")}
                            {...columnsStyles.accordionSection}
                            initialExpanded={false}
                        >
                            <Zone contentId={id} zoneName="leftContent" />
                        </ManagedAccordionSection>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...catalogContainerStyles.rightColumn}>
                    <Zone contentId={id} zoneName="rightContent" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListColumns),
    definition: {
        group: "Product List",
        displayName: "Columns",
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
